/**
 * SegmentedControl — branded single-select toggle for small enumerations
 * (sex, neuter status, mode switches). A tap-friendly, on-brand alternative to
 * a dropdown when there are 2–4 options. Reused across both consoles.
 *
 * a11y: radiogroup semantics; each segment is a radio with aria-checked.
 * e2e: pass `testId` and each segment exposes `${testId}-${value}`.
 */
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: LucideIcon;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  testId?: string;
  className?: string;
  'aria-label'?: string;
}

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  testId,
  className,
  'aria-label': ariaLabel,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex h-11 w-full items-stretch gap-1 rounded-input border-2 border-input bg-secondary/40 p-1',
        className,
      )}
    >
      {options.map((o) => {
        const selected = o.value === value;
        const Icon = o.icon;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={selected}
            data-testid={testId ? `${testId}-${o.value}` : undefined}
            onClick={() => onChange(o.value)}
            className={cn(
              'flex flex-1 items-center justify-center gap-1.5 rounded-[12px] px-2 text-sm font-semibold transition-all duration-fast',
              selected
                ? 'bg-card text-foreground shadow-clay-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {Icon && <Icon className="h-4 w-4 shrink-0" />}
            <span className="truncate">{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}
